import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Printer, Pencil, Zap } from "lucide-react";
import { KalkulierteDateien } from "@/components/KalkulierteDateien";
import { NumberField, useFieldErrors } from "@/components/DecimalInput";
import { SectionBody, SectionHead, SectionSummary } from "@/components/SectionCollapse";
import { INVALID_FORM_MSG, intOpts, parseFields, toFieldText } from "@/lib/decimal";

const num = (v, unit, digits = 2) => (v === null || v === undefined || v === "" ? "–" : `${Number(v).toLocaleString("de-DE", { maximumFractionDigits: digits })}${unit ? ` ${unit}` : ""}`);
const txt = (v) => (v === null || v === undefined || v === "" ? "–" : String(v));
const hm = (m) => (m === null || m === undefined ? "–" : `${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")} h`);
const eur = (v, cur) => (v === null || v === undefined ? "–" : Number(v).toLocaleString("de-DE", { style: "currency", currency: cur || "EUR" }));

const Field = ({ label, value, testid }) => (
  <div><div className="text-[11px] text-slate-500 uppercase tracking-wider font-mono">{label}</div><div className="text-sm font-medium text-slate-900" data-testid={testid}>{value}</div></div>
);
const calcCount = (positions) => { const n = positions.filter((p) => p.kalk).length; return n === 1 ? "1 kalkulierte Datei" : `${n} kalkulierte Dateien`; };

const rows = (w) => [
  ["Material", txt(w.material), "dp-material"], ["Farbe", txt(w.farbe), "dp-farbe"], ["Schichthöhe", num(w.schichthoehe_mm, "mm"), "dp-schichthoehe"],
  ["Wandlinien", txt(w.wandlinien), "dp-wandlinien"], ["Infill", num(w.infill_prozent, "%"), "dp-infill"], ["Stückzahl", txt(w.stueckzahl), "dp-stueckzahl"],
  ...(w.staerke ? [["Stärke", txt(w.staerke), "dp-staerke"]] : []), ...(w.duesendurchmesser ? [["Düsendurchmesser", txt(w.duesendurchmesser), "dp-duese"]] : []),
  ...(w.express !== null && w.express !== undefined ? [["Express", w.express ? <span className="inline-flex items-center gap-1 rounded bg-rose-600 px-2 py-0.5 text-white"><Zap className="w-3 h-3" />Ja</span> : "Nein", "dp-express-field"]] : []),
  ["Druckzeit", hm(w.druckzeit_min), "dp-druckzeit"], ["Materialmenge", num(w.materialmenge_g, "g"), "dp-materialmenge"],
];

// Alle optional (leer = nicht gesetzt); Ganzzahlen wie im Backend-Modell (druckparameter.py)
const OPT = { required: false };
const NUM_FIELDS = [
  ["schichthoehe_mm", "Schichthöhe (mm)", { ...OPT, min: 0, max: 10, maxDecimals: 3, decimals: 3, minDecimals: 0 }],
  ["wandlinien", "Wandlinien", intOpts(0, 100, OPT)],
  ["infill_prozent", "Infill (%)", { ...OPT, min: 0, max: 100, maxDecimals: 1, decimals: 1, minDecimals: 0 }],
  ["stueckzahl", "Stückzahl", intOpts(1, 100000, OPT)],
  ["druckzeit_min", "Druckzeit (Minuten)", intOpts(0, 1000000, OPT)],
  ["materialmenge_g", "Materialmenge (g)", { ...OPT, min: 0, max: 1000000, maxDecimals: 2, decimals: 2, minDecimals: 0 }],
];
const NUM_SPEC = Object.fromEntries(NUM_FIELDS.map(([k, , o]) => [k, o]));
const TXT_FIELDS = [["material", "Material"], ["farbe", "Farbe"], ["staerke", "Stärke"], ["duesendurchmesser", "Düsendurchmesser"]];

const EditDialog = ({ open, onOpenChange, werte, onSave }) => {
  const [f, setF] = useState(() => toFieldText(werte, NUM_SPEC));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors, clearErr] = useFieldErrors();
  useEffect(() => { setF(toFieldText(werte, NUM_SPEC)); setErrors({}); }, [werte, open]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    const p = parseFields(f, NUM_SPEC);
    if (!p.ok) { setErrors(p.errors); toast.error(INVALID_FORM_MSG); return; }
    setSaving(true);
    const body = { ...f, ...p.values };
    try { await onSave(body); onOpenChange(false); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="dp-edit-dialog">
        <DialogHeader><DialogTitle>Druckparameter bearbeiten</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {TXT_FIELDS.map(([k, l]) => <div key={k}><Label>{l}</Label><Input value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`dp-edit-${k}`} /></div>)}
          {NUM_FIELDS.map(([k, l, o]) => <div key={k}><Label htmlFor={`dp-edit-${k}`}>{l}</Label><NumberField id={`dp-edit-${k}`} align="left" opts={o} value={f[k]} error={errors[k]} onChange={(t) => { setF((x) => ({ ...x, [k]: t })); clearErr(k); }} testId={`dp-edit-${k}`} /></div>)}
          <div className="flex items-center gap-2 col-span-2"><Switch checked={!!f.express} onCheckedChange={(v) => setF({ ...f, express: v })} data-testid="dp-edit-express" /><Label>Express</Label></div>
          <div className="col-span-2"><Label>Hinweise des Kunden</Label><Textarea rows={4} value={f.hinweise ?? ""} onChange={(e) => setF({ ...f, hinweise: e.target.value })} data-testid="dp-edit-hinweise" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dp-edit-cancel">Abbrechen</Button>
          <Button onClick={save} disabled={saving} data-testid="dp-edit-save">Speichern</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const DruckparameterCard = ({ orderId, canEdit, positions = [], collapseKey }) => {
  const [dp, setDp] = useState(null);
  const [techOpen, setTechOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  useEffect(() => { api.get(`/auftraege/${orderId}/druckparameter`).then((r) => setDp(r.data)).catch(() => setDp(null)); }, [orderId]);
  const hasCalc = positions.some((p) => p.kalk);
  if (!dp?.vorhanden && !hasCalc) return null;
  if (!dp?.vorhanden) {
    return (
      <Card className="border-slate-200" data-testid="druckparameter-card">
        <CardContent className="p-5 space-y-4">
          <SectionHead k={collapseKey} label="Druckparameter" className="flex items-center gap-2"><Printer className="w-4 h-4 text-slate-500" /><h2 className="text-base font-semibold">Druckparameter</h2>
            <SectionSummary k={collapseKey}>{calcCount(positions)}</SectionSummary></SectionHead>
          <SectionBody k={collapseKey}><KalkulierteDateien positions={positions} /></SectionBody>
        </CardContent>
      </Card>
    );
  }
  const w = dp.werte || {};
  const info = dp.info || {};
  const tech = Object.entries(dp.technik || {}).filter(([k]) => !/pauschale|surcharge/i.test(k));
  const save = async (body) => {
    try { const r = await api.put(`/auftraege/${orderId}/druckparameter`, body); setDp(r.data); toast.success("Druckparameter gespeichert."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen."); throw e; }
  };
  return (
    <Card className="border-slate-200" data-testid="druckparameter-card">
      <CardContent className="p-5 space-y-4">
        <SectionHead k={collapseKey} label="Druckparameter" className="flex items-center gap-2 flex-wrap">
          <Printer className="w-4 h-4 text-slate-500" />
          <h2 className="text-base font-semibold">Druckparameter</h2>
          <SectionSummary k={collapseKey}>{[w.material, w.farbe, w.stueckzahl ? `${w.stueckzahl} Stück` : ""].filter(Boolean).join(" · ")}</SectionSummary>
          {w.express && <Badge className="bg-rose-600 text-white" data-testid="dp-express"><Zap className="w-3 h-3 mr-1" />Express</Badge>}
          {dp.bearbeitet_am && <span className="text-xs text-slate-500" data-testid="dp-edited">bearbeitet von {dp.bearbeitet_von}</span>}
          {canEdit && <Button size="sm" variant="outline" className="ml-auto" onClick={() => setEditOpen(true)} data-testid="dp-edit-button"><Pencil className="w-3.5 h-3.5 mr-1" /> Bearbeiten</Button>}
        </SectionHead>
        <SectionBody k={collapseKey} className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {rows(w).map(([l, v, t]) => <Field key={t} label={l} value={v} testid={t} />)}
        </div>
        <KalkulierteDateien positions={positions} />
        <div className="rounded-md border-2 border-amber-300 bg-amber-50 p-3" data-testid="dp-hinweise-box">
          <div className="text-xs font-semibold uppercase tracking-wider text-amber-800 mb-1">Hinweise des Kunden</div>
          <div className="text-sm text-amber-950 whitespace-pre-wrap" data-testid="dp-hinweise">{w.hinweise || "–"}</div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <Field label="Richtpreis Website (unverbindlich)" value={`${eur(info.richtpreis_netto, info.waehrung)} netto · ${eur(info.richtpreis_brutto, info.waehrung)} brutto`} testid="dp-richtpreis" />
          <Field label="AGB akzeptiert" value={info.agb_akzeptiert === true ? "Ja" : info.agb_akzeptiert === false ? "Nein" : "–"} testid="dp-agb" />
          <div className="sm:col-span-2"><Field label="Nextcloud-Ordner" value={<span className="font-mono text-xs break-all">{txt(info.nextcloud_ordner)}</span>} testid="dp-nextcloud" /></div>
        </div>
        <div>
          <button type="button" onClick={() => setTechOpen(!techOpen)} className="flex items-center gap-1 text-xs text-slate-600" data-testid="dp-tech-toggle">
            {techOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />} Technische Details
          </button>
          {techOpen && (
            <div className="mt-2 space-y-3 text-xs" data-testid="dp-tech">
              <table className="w-full"><tbody>
                {tech.map(([k, v]) => (
                  <tr key={k} className="border-t border-slate-100"><td className="py-1 pr-3 font-mono text-slate-500">{k}</td><td className="py-1 break-all">{typeof v === "object" ? JSON.stringify(v) : String(v)}</td></tr>
                ))}
                {!tech.length && <tr><td className="py-1 text-slate-500">–</td></tr>}
              </tbody></table>
              {dp.original && (
                <div data-testid="dp-original">
                  <div className="font-semibold text-slate-700 mb-1">Ursprünglich von der Website</div>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    {rows(dp.original).map(([l, v, t]) => <div key={t}><span className="text-slate-500">{l}:</span> {v}</div>)}
                  </div>
                  <div className="mt-1 whitespace-pre-wrap"><span className="text-slate-500">Hinweise:</span> {dp.original.hinweise || "–"}</div>
                </div>
              )}
            </div>
          )}
        </div>
        </SectionBody>
      </CardContent>
      {canEdit && <EditDialog open={editOpen} onOpenChange={setEditOpen} werte={w} onSave={save} />}
    </Card>
  );
};
